import { useState, useMemo, useEffect } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { PageHeader } from "@shared/components/PageHeader";
import { useCategory, useConsumerRelation, useSubCategory } from "@features/cx/consumerOnboarding/hooks";
import { PhoneInputComponent } from "@shared/components/PhoneInputComponent";

interface ConsumerDetailProps {
  onDataChange?: (data: any) => void;
  onValidationChange?: (isValid: boolean) => void;
  consumerData?: any;
  onFormReady?: (form: any) => void;
}

export function ConsumerDetail({ onDataChange, onValidationChange, consumerData, onFormReady }: ConsumerDetailProps) {
  const { data: categoryData } = useCategory();
  const [subCategoryList, setSubCategoryList] = useState<string>("");
  const [isAddingCoApplicant, setIsAddingCoApplicant] = useState(false);
  const { data: subCategoryData } = useSubCategory(subCategoryList);
  const { data: consumerRelationData } = useConsumerRelation();

  const relationshipOptions = useMemo(
    () =>
      consumerRelationData?.result?.map((item: any) => ({
        label: item.value,
        value: item.key,
      })) || [],
    [consumerRelationData]
  );

  const categoryOptions = useMemo(
    () =>
      categoryData?.result?.map((category) => ({
        value: category.code,
        label: category.name,
      })) || [],
    [categoryData]
  );

  const subCategoryOptions = useMemo(
    () =>
      subCategoryData?.result?.map((subCategory) => ({
        value: subCategory.code,
        label: subCategory.name,
      })) || [],
    [subCategoryData]
  );

  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "firstName",
        label: "First Name",
        type: "text",
        required: true,
        maxLength: 50,
        placeholder: "Enter first name",
        helperText: "Enter your legal first name",
        tooltipText: "Your legal first name as it appears on official documents",
        showHelperTooltip: true,
        className: "col-span-4",
      },
      {
        name: "lastName",
        label: "Last Name",
        type: "text",
        required: true,
        maxLength: 50,
        placeholder: "Enter last name",
        helperText: "Enter your legal last name",
        tooltipText: "Your legal last name as it appears on official documents",
        showHelperTooltip: true,
        className: "col-span-4",
      },
      {
        name: "email",
        label: "Email",
        type: "email",
        required: true,
        placeholder: "example@email.com",
        helperText: "We'll use this for account notifications",
        tooltipText: "Primary email address for account communication and notifications",
        showHelperTooltip: true,
        className: "col-span-4",
      },
      {
        name: "phoneNumber",
        label: "Phone Number",
        type: "country-phone",
        required: true,
        placeholder: "+1 (555) 000-0000",
        helperText: "Include country code for international numbers",
        tooltipText: "Contact phone number including country code",
        showHelperTooltip: true,
        className: "col-span-4",
      },
      {
        name: "accountCategory",
        label: "Category",
        type: "select",
        required: true,
        options: categoryOptions,
        placeholder: "Select category...",
        helperText: "Choose the account type",
        tooltipText: "Select the primary account category that best describes your account type",
        showHelperTooltip: true,
        className: "col-span-4",
      },
      {
        name: "accountSubCategory",
        label: "Sub-Category",
        type: "select",
        required: true,
        options: subCategoryOptions,
        disabled: !subCategoryList,
        placeholder: "Select sub-category...",
        helperText: "Specify the account sub-type",
        tooltipText: "Choose a specific sub-category within your selected account category",
        showHelperTooltip: true,
        className: "col-span-4",
      },
      {
        name: "ssnTaxId",
        label: "SSN/Tax ID",
        type: "text",
        required: false,
        placeholder: "Enter SSN or Tax ID",
        helperText: "SSN for Individual, Tax ID for Business",
        tooltipText: "Social Security Number for individuals or Tax ID for business entities",
        showHelperTooltip: true,
        className: "col-span-4",
      },
      {
        name: "dateOfBirth",
        label: "Date of Birth",
        type: "datepicker",
        required: false,
        placeholder: "Enter date of birth",
        helperText: "Enter your date of birth",
        tooltipText: "Your date of birth as it appears on official documents",
        showHelperTooltip: true,
        className: "col-span-4",
      },
      {
        name: "vipDesignation",
        label: "VIP Designation",
        type: "checkbox",
        required: false,
        helperText: "Mark this account for priority service and support",
        tooltipText: "Enable VIP status for priority handling and dedicated support",
        showHelperTooltip: true,
        className: "col-span-6",
      },
      {
        name: "addCoApplicant",
        label: "Add co-applicant",
        type: "checkbox",
        required: false,
        helperText: "Include an additional person on this application",
        tooltipText: "Add a co-applicant to share account responsibilities",
        showHelperTooltip: true,
        className: "col-span-6",
      },
       {
        name: "relationShip",
        label: "Relationship to Primary",
        type: "select",
        required: isAddingCoApplicant,
        options: relationshipOptions,
        helperText:"Defines the relationship between the primary applicant and the co-applicant",
        group: "Co-applicant Details",
        groupColorNeeded: false,
        groupBorderNeeded: false,
        className: "col-span-6",
        showWhen: (formValues) => formValues.addCoApplicant === true,
      },
      {
        name: "coApplicantFirstName",
        label: "First Name",
        type: "text",
        required: isAddingCoApplicant,
        maxLength: 50,
        placeholder: "Enter first name",
        group: "Co-applicant Details",
        groupColorNeeded: false,
        groupBorderNeeded: false,
        helperText: "Enter co-applicant's legal first name",
        showWhen: (formValues) => formValues.addCoApplicant === true,
        className: "col-span-6",
      },
      {
        name: "coApplicantLastName",
        label: "Last Name",
        type: "text",
        required: isAddingCoApplicant,
        maxLength: 50,
        placeholder: "Enter last name",
        group: "Co-applicant Details",
        groupColorNeeded: false,
        groupBorderNeeded: false,
        helperText: "Enter co-applicant's legal last name",
        showWhen: (formValues) => formValues.addCoApplicant === true,
        className: "col-span-6",
      },
      {
        name: "coApplicantEmail",
        label: "Email",
        type: "email",
        required: isAddingCoApplicant,
        placeholder: "example@email.com",
        group: "Co-applicant Details",
        groupColorNeeded: false,
        groupBorderNeeded: false,
        helperText: "Co-applicant's email for notifications",
        showWhen: (formValues) => formValues.addCoApplicant === true,
        className: "col-span-6",
      },
      {
        name: "coApplicantPhoneNumber",
        label: "Phone Number",
        type: "country-phone",
        required: isAddingCoApplicant,
        placeholder: "+1 (555) 000-0000",
        group: "Co-applicant Details",
        groupColorNeeded: false,
        groupBorderNeeded: false,
        helperText: "Co-applicant's contact number",
        showWhen: (formValues) => formValues.addCoApplicant === true,
        className: "col-span-6",
      },
     
    ],
    [subCategoryOptions, relationshipOptions, categoryOptions, subCategoryList, isAddingCoApplicant]
  );
const defaultFormValues = useMemo(() => {
  if (!consumerData?.result) return {};

  const data = consumerData.result;
  
  return {
    firstName: data.firstName || "",
    lastName: data.lastName || "",
    email: data.email || "",
    phoneNumber: data.contactNumber || "",
    accountCategory: data.category || "",
    accountSubCategory: data.subCategory || "",
    ssnTaxId: data.ssnEncrypted || "",
    vipDesignation: data.isVip || false,
    dateOfBirth: data.dateOfBirth || "",
    addCoApplicant: !!data.additionalData?.secondaryConsumer?.firstName,

    // Co applicant (if exists)
    coApplicantFirstName: data.additionalData?.secondaryConsumer?.firstName || "",
    coApplicantLastName: data.additionalData?.secondaryConsumer?.lastName || "",
    coApplicantEmail: data.additionalData?.secondaryConsumer?.email || "",
    coApplicantPhoneNumber: data.additionalData?.secondaryConsumer?.contactNumber || "",
    relationShip: data.additionalData?.secondaryConsumer?.secondaryRelationship || "",
  };
}, [consumerData]);;

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
  });

   useEffect(() => {
    if (onFormReady) {
      onFormReady(form);
    }
  }, [form, onFormReady]);

useEffect(() => {
  if (!consumerData?.result) return;

  // Step 1: set category first (which affects conditional fields)
  const data = consumerData.result;
  setSubCategoryList(data.category || "");
  setIsAddingCoApplicant(!!data.additionalData?.secondaryConsumer?.firstName);

  // Step 2: allow fields to mount based on showWhen
  setTimeout(() => {
    FormService.populateForm(form, defaultFormValues, formFields);
    const currentValues = form.getValues();
    onDataChange(buildPayload(currentValues));
  }, 0);
}, [consumerData, defaultFormValues, form]);

  const buildPayload = (formValues: any) => {

    const getCategoryLabel = () => {
    return categoryOptions.find(option => option.value === formValues.accountCategory)?.label || "";
  };

  const getSubCategoryLabel = () => {
    return subCategoryOptions.find(option => option.value === formValues.accountSubCategory)?.label || "";
  };
    const getRelationshipLabel = () => {
    return relationshipOptions.find(option => option.value == formValues.relationShip)?.label || "";
  };
  const relationshipLabel = getRelationshipLabel();
    const payload: any = {
      first_name: formValues.firstName || "",
      last_name: formValues.lastName || "",
      email: formValues.email || "",
      phone_number: formValues.phoneNumber || "",
      account_category: formValues.accountCategory || "",
       account_category_display: getCategoryLabel(),
      account_sub_category: formValues.accountSubCategory || "",
         account_sub_category_display: getSubCategoryLabel(),
      ssn: formValues.ssnTaxId || "",
      vip_designation: formValues.vipDesignation || false,
      date_of_birth: formValues.dateOfBirth || "",
      add_co_applicant: formValues.addCoApplicant || false,
    };

    if (formValues.addCoApplicant) {
      payload.co_applicant = {
        first_name: formValues.coApplicantFirstName || "",
        last_name: formValues.coApplicantLastName || "",
        email: formValues.coApplicantEmail || "",
        phone_number: formValues.coApplicantPhoneNumber || "",
        relationship: formValues.relationShip || "",
        relationship_display: relationshipLabel,
      };
    }

    return payload;
  };

const handleFieldChange = (fieldName: string, value: any) => {
  if (fieldName === "accountCategory") {
    setSubCategoryList(value);
    if (!value) {
      // Use shouldValidate: false and shouldTouch: false to prevent triggering onChange
      form.setValue("accountSubCategory", "", { 
        shouldValidate: false, 
        shouldDirty: false 
      });
    }
  }

  if (fieldName === "addCoApplicant") {
    setIsAddingCoApplicant(value);
  }

  // if (fieldName === "addCoApplicant" && !value) {
  //   // Clear co-applicant fields without triggering onChange
  //   form.setValue("coApplicantFirstName", "", { shouldValidate: false, shouldDirty: false });
  //   form.setValue("coApplicantLastName", "", { shouldValidate: false, shouldDirty: false });
  //   form.setValue("coApplicantEmail", "", { shouldValidate: false, shouldDirty: false });
  //   form.setValue("coApplicantPhoneNumber", "", { shouldValidate: false, shouldDirty: false });
  //   form.setValue("relationShip", "", { shouldValidate: false, shouldDirty: false });
  // }
  

  if (onDataChange) {
    const currentValues = form.getValues();
    onDataChange(buildPayload(currentValues));
  }
};

  // Watch form validation state
 useEffect(() => {
  if (!onValidationChange) return;

  const subscription = form.watch(() => {
    const hasErrors = Object.keys(form.formState.errors)?.length > 0;
    onValidationChange(!hasErrors);
  });

  return () => subscription.unsubscribe();
}, [form, onValidationChange]);

  // Send initial data to parent - only once on mount
  // useEffect(() => {
  //   if (onDataChange ) {
  //     const currentValues = form.getValues();
  //     onDataChange(buildPayload(currentValues));
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []); // Empty dependency array - only run once on mount

  return (
    <div className="space-y-6">
      <div className="mb-4 flex items-center gap-4">
        <div className="flex flex-col">
          <PageHeader
            title="Consumer Details"
            subtitle="Provide personal and account information for the primary applicant"
          />
        </div>
      </div>

      <DynamicForm
        fields={formFields}
        form={form}
        onSubmit={() => {}}
        onFieldChange={handleFieldChange}
        gridColumns={12}
        config={{
          showSubmitButton: false,
          showResetButton: false,
          classes: FIELD_CLASSES,
        }}
      />
    </div>
  );
}